import toggleClass from './elements/toggleClass';
import addPassiveEventListener from './events/addPassiveEventListener';
import addStartupScript from './events/addStartupScript';
import addTargetEventListener from './events/addTargetEventListener';
import getScrollPosition from './media/getScrollPosition';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import forEachSelector from './elements/forEachSelector';
import getCookieByName from './storage/getCookieByName';

import 'element-closest-polyfill';

import { isSignInVisible } from './feature_flag/launchdarkly';

const HAMBURGER = 'mc-navbar__hamburger';
const HEADER = 'header';
const HEADER_MINIMIZED = `${HEADER}--minimized`;
const HTML_WITH_HEADER = 'html--mc-navbar';
const HTML_WITH_HEADER_OPEN = 'html--mc-navbar-open';
const HTML_WITH_HEADER_FIXED = 'html--mc-navbar-fixed';
const HTML_WITH_HEADER_RELATIVE = 'html--mc-navbar-relative';
const SIGN_IN_VISIBLE       = 'mc-navbar-sign-in__visible';
const ECOMM_LINK_VISIBLE     = 'mc-navbar-ecomm-link__visible';
const ECOMM_LINK_BUTTON      = 'mc-nav__ecomm-link-button';
const NAVBAR_NAV            = 'mc-navbar__nav';
const SUBNAV                = 'subnav-bar';
const SUBNAV_CTA            = 'subnav-cta-bar';
const NAVBAR                = 'mc-navbar';
const ECOMM_NAV             = 'mc-navbar--ecomm-test';
const ECOMM_SUBNAV          = 'ecomm-subnav';
const LEGACY_NAV            = 'mc-navbar--legacy-nav';
const NAVBAR_SEARCHING      = `${NAVBAR}--searching`;
const SEARCH_INPUT          = 'input[type=search]';
const SEARCH__CLOSE         = 'mc-search-form__close';
const MINIMUM_HEIGHT        = 676;  // height of the browser window in pixels so that we can unpin the menu, see MEX-10011

let leadformStarted = false;
let signInButtonEnabled = false;
let ecommLinkButtonEnabled = false;
const shouldMinimize = () => leadformStarted || getScrollPosition() > window.innerHeight * .5;

// querySelector shortcuts with null safety check
const $ = (selector, el = document) => el && el.querySelector(selector);
const queryHeader = (el = document) => $(`.${HEADER}`, el);
const querySubnav = (el = document) => $(`.${SUBNAV}`, el);
const queryEcommNav = (el = document) => $(`.${ECOMM_NAV}`, el);
const queryEcommSubnav = (el = document) => $(`.${ECOMM_SUBNAV}`, el);
const queryLegacyNav = (el = document) => $(`.${LEGACY_NAV}`, el);
const querySubnavCta = (el = document) => $(`.${SUBNAV_CTA}`, el);
const queryHamburger = (el = document) => $(`.${HAMBURGER}`, el);
const queryNavbarNav = (el = document) => $(`.${NAVBAR_NAV}`, el);
const queryEcommLinkButton = (el = document) => $(`.${ECOMM_LINK_BUTTON}`, el);
const queryNavbar = (el = document) => $(`.${NAVBAR}`, el);
const htmlElement = document.documentElement;

function toggleDrawer(force) {
  toggleClass(htmlElement, HTML_WITH_HEADER_OPEN, force);

  if (htmlElement.classList.contains(HTML_WITH_HEADER_OPEN)) {
    hideSignInButton();
    disableBodyScroll(queryNavbarNav());
  } else {
    if (window.innerWidth >= 630 ){
      enableSignInButton();
    }
    clearAllBodyScrollLocks();
  }
}

/**
  * Shows the sign in button if the feature flag is enabled 
  * and the sign in link when feature flag is off. 
 */
async function signIn() {
  if (await isSignInVisible()) {
    signInButtonEnabled = true;
    toggleSignInButton();
  } else {
    toggleSignInLink();
  }
}

function enableLegacyNav() {
  const ecommNav = queryEcommNav();
  const ecommSubnav = queryEcommSubnav();
  const legacyNav = queryLegacyNav();
  
  ecommLinkButtonEnabled = false;
  legacyNav.style.display = 'block';
  ecommSubnav.style.display = 'none';
  ecommNav.style.display = 'none';
}

function enableEcommNav() {
  const subnavBar = querySubnav();
  const ecommNav = queryEcommNav();
  const legacyNav = queryLegacyNav();
  const ecommSubnav = queryEcommSubnav();
  const subnavCta = querySubnavCta();
  const ecommLinkButton = queryEcommLinkButton();

  if (ecommNav && legacyNav) {
    ecommNav.style.display = 'block';
    ecommSubnav.style.display = 'block';
    legacyNav.style.display = 'none';
  }
  ecommLinkButtonEnabled = true;

  if (subnavBar) {
    subnavBar.classList.remove('subnav-bar--always-visible');
    subnavBar.style.display = 'none';
  }

  if (subnavCta) {
    subnavCta.style.display = 'none'; 
  }

  if (ecommLinkButton) {
    signInButtonEnabled = true;
    toggleSignInButton();
    toggleEcommLinkButton();
  }
}

function shouldShowEcommNav() {
  const pathName = window?.location?.pathname;
  const savedEcommCookieValue = getCookieByName('ecomm_return') || 'false';
  let showEcommNav = false;
  
  if (savedEcommCookieValue && savedEcommCookieValue === 'true') {
    showEcommNav = true;
  }
  
  if (pathName && pathName.startsWith('/school-solutions')) {
    showEcommNav = false;
  }

  return showEcommNav;
}

async function isEcommNavEnabled() {
  const authHeader = document.querySelector('.header--auth');
  const vt4sHeader = document.querySelector('.header--vt4s');
  
  let showEcommNav = shouldShowEcommNav();

  if (showEcommNav && !authHeader && !vt4sHeader) {
    enableEcommNav();
  }
  else {
    enableLegacyNav();
  }

  fixNavbar();
}

/**
  * Toggles the visibility of the sign in button based on the window width.
*/
function toggleSignInButton() {
  if (signInButtonEnabled) {
    if (window.innerWidth >= 630) {
      enableSignInButton();
    } else {
      hideSignInButton();
    }
  }
}

function toggleEcommLinkButton() {
  if (ecommLinkButtonEnabled) {
    if (window.innerWidth >= 1280) {
      enableEcommLinkButton();
    } else {
      hideEcommLinkButton();
    }
  }
}

/**
  * Toggles the visibility of the sign in link based on the window width. 
*/
function toggleSignInLink() {
  const signInLinks = document.querySelectorAll('.mc-navbar__nav-sign-in');
  if (!signInButtonEnabled && signInLinks) {
    if (window.innerWidth < 1280) {
      signInLinks.forEach((link) => link.classList.remove(SIGN_IN_VISIBLE));
    }
    else {
      signInLinks.forEach((link) => link.classList.add(SIGN_IN_VISIBLE));
    }
  }
}


/**
 * Enables the sign in button
*/
function enableSignInButton() {
  const signInButtons = document.querySelectorAll('.mc-nav__sign-in-button');
  if (signInButtons && signInButtonEnabled) {
    signInButtons.forEach((button) => button.classList.add(SIGN_IN_VISIBLE));
  }
}

/**
 * Disables the sign in button
*/
function hideSignInButton() {
  const signInButtons = document.querySelectorAll('.mc-nav__sign-in-button');
  if (signInButtons && signInButtonEnabled) {
    signInButtons.forEach((button) => button.classList.remove(SIGN_IN_VISIBLE));
  }
}

/**
 * Disables the ecomm link button
*/
function hideEcommLinkButton() {
  const ecommLinkButton = queryEcommLinkButton();
  if (ecommLinkButton && ecommLinkButtonEnabled) {
    ecommLinkButton.classList.remove(ECOMM_LINK_VISIBLE);
  }
}

/**
 * enables the ecomm link button
*/
function enableEcommLinkButton() {
  const ecommLinkButton = queryEcommLinkButton();
  if (ecommLinkButton && ecommLinkButtonEnabled) {
    ecommLinkButton.classList.add(ECOMM_LINK_VISIBLE);
  }
}

/**
 * Changes the navbar to be fixed or relative (see MEX-10011) instead of sticky
 * for better compatibility.
 * For this we must calculate the height of the navbar and then update the main
 * accordingly.
 */
function fixNavbar() {
    // if the window is too "short", we make the header relative. See MEX-10011
    // otherwise fall through to the original behavior.
    if (isDesktopBrowser() && window.innerHeight < MINIMUM_HEIGHT ) {
        toggleClass(htmlElement, HTML_WITH_HEADER_FIXED, false);
        toggleClass(htmlElement, HTML_WITH_HEADER_RELATIVE, true);
        resetHeaderOffsetPaddingToOrigin();
    } else {
        toggleClass(htmlElement, HTML_WITH_HEADER_FIXED, true);
        toggleClass(htmlElement, HTML_WITH_HEADER_RELATIVE, false);
        resetHeaderOffsetPadding();
    }
}

/**
 * Determine if we're using a desktop layout; This is a custom
 * css property that is set in navbar__desktop.sass
 */
function isDesktopBrowser() {
    const navbar = queryNavbar();

    // if we can't find the navbar, abort
    if (!(navbar instanceof Element)) {
        return false;
    }

    //  --navbar-layout is set in navbar__desktop.sass
    const navbarLayout = getComputedStyle(navbar).getPropertyValue('--navbar-layout').replace(/\W/g, '');
    return navbarLayout === 'desktop';
}

/**
 * Resets body padding for fixed header, except if we are minimized, scrolled down,
 * and the leadform is not started
 */
function resetHeaderOffsetPadding() {
  const header = queryHeader();

  if (header) {
    const navHeight = calculateHeaderHeight(leadformStarted);
    const main = document.querySelector('main');
    main.style.marginTop = navHeight + 'px';
  }
}

/**
 * Resets the header back to the origin, a workaround for MEX-10011.
 * On "short" browsers, we want to unpin the header menus.
 */
function resetHeaderOffsetPaddingToOrigin() {
  const header = queryHeader();

  if (header) {
    const main = document.querySelector('main');
    main.style.marginTop = '0px';
  }

}

/**
 * Checks if the hamburger menu is hidden (because we are on desktop),
 * and if so automatically closes the navbar.
 */
function checkCloseDrawer() {
  const hamburger = queryHamburger();
  if (hamburger && getComputedStyle(hamburger).display === 'none') {
    toggleDrawer(false);
  }
}

/**
 * Calculates what the height of the header should be in either minimized or regular
 * state, without waiting for transitions. Does this by cloning the node and measuring the clone.
 * @param {Boolean} minimized - Whether to measure minimized state or regular state
 */
function calculateHeaderHeight(minimized = false) {
  const header = queryHeader();
  const clone = header.cloneNode(true);
  toggleClass(clone, HEADER_MINIMIZED, minimized);
  clone.style.visibility = 'hidden'; // do not show to user
  document.body.appendChild(clone);
  const offsetHeight = clone.offsetHeight;
  document.body.removeChild(clone);
  return offsetHeight;
}

/**
 * Sets the header as minimized or not, depending on whether the user has scrolled down
 * and whether the leadform is started (and not back on the welcome panel).
 */
function checkMinimizeHeader() {
  if (ecommLinkButtonEnabled) {
    return;
  }
  const header = queryHeader();
  const subnav = querySubnav(header);
  if (header && subnav) {
    if (header.classList.contains(HEADER_MINIMIZED) !== shouldMinimize()) {
      // ensure we have fixed position and margin has been set
      fixNavbar();

      // we can minimize after the above is applied
      requestAnimationFrame(() => toggleClass(header, HEADER_MINIMIZED, shouldMinimize()));
    }
  }
}

function onScroll() {
  checkMinimizeHeader();
}

function onResize() {
  // always fix navbar on resize, Ensure that we can unpin it if the window is too short (MEX-10011)
  fixNavbar();
  checkCloseDrawer();
  checkMinimizeHeader();
  setAppHeight();
  toggleSignInButton();
  toggleSignInLink();
  toggleEcommLinkButton();
}

function setAppHeight() {
  // fix for iOS 100vh bug (100vh overlaps the bottom tab bar, it's a "feature")
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
}

function onStart() {
  if (!leadformStarted) {
    leadformStarted = true;
    checkMinimizeHeader();
    requestAnimationFrame(resetHeaderOffsetPadding);
  }
}

function onBack() {
  if (document.querySelector('.lead-constructor__panel--welcome.active') && leadformStarted) {
    leadformStarted = false;
    checkMinimizeHeader();
    requestAnimationFrame(resetHeaderOffsetPadding);
  }
}

/**
 * Adds a handler on the document that checks for taps on the Hamburger menu
 * and toggles the navbar menu. By listening on the document, we can set up this
 * script at the START of page load instead of waiting until the whole document is ready.
 */
function addDrawerTapHandler() {
  ['mouseup', 'touchend'].forEach(eventName => {
    document.addEventListener(eventName, function(event) {
      if (event.target.closest(`.${HAMBURGER}`)) {
        toggleDrawer();
        event.preventDefault();
      } else if (!event.target.closest(`.${NAVBAR_NAV}`)) {
        toggleDrawer(false);
      }
    });
  });
}

// Focusing the search box enables search mode
function focusOnSearch() {
  const navbar = this.closest(`.${NAVBAR}`);
  navbar && navbar.classList.add(NAVBAR_SEARCHING);
}

// Clicking the search Back link closes the search
function handleSearchCloseClick(event) {
  event.preventDefault();
  forEachSelector(`.${NAVBAR_SEARCHING}`, (navbar) => navbar.classList.remove(NAVBAR_SEARCHING));
}

addStartupScript(() => {
  fixNavbar();
  signIn();
  isEcommNavEnabled();
});

addDrawerTapHandler();
addPassiveEventListener(document, 'scroll', onScroll);
addPassiveEventListener(window, 'resize', onResize);
addTargetEventListener('click', '.js-get-started, .js-lead-constructor__next', onStart);
addTargetEventListener('click', '.lead-constructor__back', onBack);
addTargetEventListener('focusin', `.${NAVBAR} ${SEARCH_INPUT}`, focusOnSearch);
addTargetEventListener('click', `.${SEARCH__CLOSE}`, handleSearchCloseClick);

toggleClass(document.documentElement, HTML_WITH_HEADER, true);
setAppHeight();
